import { useMemo, useState } from 'react';
import { useHistory } from '@react-router';
import { LeadStateV2 } from '@graphql/generated';
import { useTranslation } from '@shared/core';
import { useEventHotelRoomBlocksSubmitted } from '@apps/admin/routes/Planning/hooks/useEventHotelRoomBlocks';
import { HOTEL_SELECTION_CONFIRM_REQUEST_LOCAL_STORAGE_KEY } from '@apps/admin/routes/Planning/routes/HotelSelection/context/HotelSelectionContext/HotelSelectionContext.const';
import { LEAD_RE_ENGAGEMENT_POPUP_ITEM_CLOSED, popupImages, ReEngagementPopupItemTypes } from '../constants/ReEngagementPopup.const';
import { HOTEL_SELECTION_LOCAL_STORAGE_KEY } from '@apps/admin/routes/Planning/routes/HotelSelection/context/HotelSelectionCartContext/HotelSelectionCart.const';
import { HotelListItemCart } from '@apps/admin/routes/Planning/routes/HotelSelection/types/HotelSelection.types';
import { useDashboardTelemetry } from '@apps/admin/routes/Dashboard/Dashboard.telemetry';
import usePrintNotification from './usePrintNotification';

type useReEngagementPopupProps = {
  eventId?: string;
  eventHandle: string;
};

const useReEngagementPopup = ({ eventId, eventHandle }: useReEngagementPopupProps) => {
  const history = useHistory();
  const { t } = useTranslation('adminDashboardV2');
  const popuText = t('reEngagementPopup');

  const { leadStateV2 } = useEventHotelRoomBlocksSubmitted(eventId ?? '');
  const { reEngagementPopupCTAClicked } = useDashboardTelemetry();

  const [closedItemMap, setClosedItemMap] = useState({
    [ReEngagementPopupItemTypes.HOTEL_BLOCK]: localStorage.getItem(LEAD_RE_ENGAGEMENT_POPUP_ITEM_CLOSED(eventId || '', ReEngagementPopupItemTypes.HOTEL_BLOCK)) ?? false,
    [ReEngagementPopupItemTypes.PRINT]: localStorage.getItem(LEAD_RE_ENGAGEMENT_POPUP_ITEM_CLOSED(eventId || '', ReEngagementPopupItemTypes.PRINT)) ?? false
  });

  const canShowRoomBlockToast = useMemo(
    () =>
      localStorage.getItem(HOTEL_SELECTION_CONFIRM_REQUEST_LOCAL_STORAGE_KEY(eventId || '')) &&
      (JSON.parse(localStorage.getItem(HOTEL_SELECTION_LOCAL_STORAGE_KEY(eventId || '')) ?? '[]') as HotelListItemCart[]).length > 0 &&
      leadStateV2 === LeadStateV2.Created,
    [eventId, leadStateV2]
  );
  const isRoomBlockToastEnabled = !closedItemMap[ReEngagementPopupItemTypes.HOTEL_BLOCK] && canShowRoomBlockToast;

  const printItem = usePrintNotification({ isClosed: !!closedItemMap[ReEngagementPopupItemTypes.PRINT] });

  const items = useMemo(
    () => [
      {
        key: ReEngagementPopupItemTypes.HOTEL_BLOCK,
        title: popuText.hotelBlock.title(),
        canShow: isRoomBlockToastEnabled,
        imageUrl: popupImages[ReEngagementPopupItemTypes.HOTEL_BLOCK],
        subtitle: popuText.hotelBlock.subTitle({
          numberOfHotels: (JSON.parse(localStorage.getItem(HOTEL_SELECTION_LOCAL_STORAGE_KEY(eventId || '')) ?? '[]') as HotelListItemCart[]).length ?? 0
        }),
        onClick: () => {
          reEngagementPopupCTAClicked(ReEngagementPopupItemTypes.HOTEL_BLOCK, 'clicked');
          history.push(`/${eventHandle}/edit/planning/hotelblock`);
        }
      },
      ...(printItem ? [printItem] : [])
    ],
    [popuText.hotelBlock, isRoomBlockToastEnabled, eventId, printItem, reEngagementPopupCTAClicked, history, eventHandle]
  );

  const canRender = useMemo(() => items.some(item => item.canShow), [items]);

  const handleRemove = (key: ReEngagementPopupItemTypes) => {
    reEngagementPopupCTAClicked(key, 'dismissed');
    setClosedItemMap(prev => ({
      ...prev,
      [key]: true
    }));
    localStorage.setItem(LEAD_RE_ENGAGEMENT_POPUP_ITEM_CLOSED(eventId || '', key), 'true');
  };

  return {
    canRender,
    items,
    handleRemove
  };
};

export default useReEngagementPopup;
